import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const C = {
  navy:    '#0f172a',
  ink:     '#1e293b',
  muted:   '#64748b',
  border:  '#e2e8f0',
  indigo:  '#4f46e5',
  indigoXL:'#eef2ff',
  snow:    '#f8fafc',
  white:   '#ffffff',
};

const Section = ({ id, title, children }) => (
  <section id={id} style={{ paddingBottom: 48, borderBottom: `1px solid ${C.border}`, marginBottom: 48 }}>
    <h2 style={{ fontSize: 'clamp(1.1rem,2vw,1.35rem)', fontWeight: 800, color: C.navy, margin: '0 0 16px', letterSpacing: '-0.02em' }}>{title}</h2>
    {children}
  </section>
);

const P = ({ children }) => (
  <p style={{ fontSize: '0.92rem', color: C.muted, lineHeight: 1.85, margin: '0 0 14px' }}>{children}</p>
);

const Ul = ({ items }) => (
  <ul style={{ margin: '0 0 14px', paddingLeft: 0, listStyle: 'none' }}>
    {items.map((item, i) => (
      <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '5px 0', fontSize: '0.9rem', color: C.muted, lineHeight: 1.7 }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.indigo, marginTop: 9, flexShrink: 0 }} />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Highlight = ({ label, children }) => (
  <div style={{ background: C.indigoXL, border: `1px solid #c7d2fe`, borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
    {label && <div style={{ fontSize: '0.62rem', fontWeight: 800, color: C.indigo, textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: 6 }}>{label}</div>}
    <div style={{ fontSize: '0.88rem', color: C.ink, lineHeight: 1.75 }}>{children}</div>
  </div>
);

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', background: C.white, color: C.navy, minHeight: '100vh' }}>

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${C.border}`, padding: '0 clamp(20px,5vw,64px)', height: 58, display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 100 }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          <img src="/logo192.png" alt="AssessMonk" style={{ width: 28, height: 28, borderRadius: 8, objectFit: 'contain' }} />
          <span style={{ fontWeight: 800, fontSize: '0.95rem', color: C.navy }}>AssessMonk</span>
        </Link>
        <button onClick={() => navigate('/app')} style={{ padding: '7px 18px', borderRadius: 9, fontWeight: 700, fontSize: '0.82rem', background: C.indigo, color: '#fff', border: 'none', cursor: 'pointer' }}>
          Try AI Assessment
        </button>
      </nav>

      {/* Hero */}
      <div style={{ background: `linear-gradient(to bottom, ${C.indigoXL}, ${C.white})`, padding: 'clamp(48px,8vw,80px) clamp(20px,5vw,64px) 0' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: C.indigoXL, border: '1px solid #c7d2fe', borderRadius: 99, padding: '5px 14px', fontSize: '0.7rem', fontWeight: 700, color: C.indigo, marginBottom: 20, letterSpacing: '0.03em' }}>
            Legal · Privacy
          </div>
          <h1 style={{ fontSize: 'clamp(1.9rem,4vw,2.8rem)', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.03em', margin: '0 0 16px' }}>
            Privacy Policy
          </h1>
          <p style={{ fontSize: '1rem', color: C.muted, lineHeight: 1.75, maxWidth: 600, margin: '0 0 24px' }}>
            AssessMonk is built on trust. This policy explains exactly what data we collect, how it is used, and the choices you have over your information.
          </p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '0.78rem', color: C.muted, fontWeight: 600, background: C.snow, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 14px', marginBottom: 48 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
            Last updated: 23 April 2026
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '48px clamp(20px,5vw,64px) 80px', display: 'grid', gridTemplateColumns: '1fr', gap: 0 }}>

        <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 12, padding: '14px 18px', marginBottom: 16, fontSize: '0.88rem', color: '#78350f', lineHeight: 1.75, fontWeight: 600 }}>
          AssessMonk is an educational platform for physiotherapy students and clinicians. It is not a medical records system and must not be used to store real patient health records. All clinical data entered should be anonymised or fictional.
        </div>

        <Highlight label="Summary">
          AssessMonk collects the minimum data necessary to operate the platform. We do not sell your data. We do not share personal clinical information with third parties for advertising. Your saved assessments belong to you.
        </Highlight>

        <Section id="who-we-are" title="1. Who We Are">
          <P>AssessMonk is a healthcare education platform designed for physiotherapy students and clinicians. It provides AI-assisted clinical assessment tools for educational and professional development purposes.</P>
          <P>When this policy refers to "AssessMonk", "we", "us", or "our", it refers to the AssessMonk platform and its operators.</P>
          <P>If you have questions about this policy, please contact us at <a href="mailto:assessmonk@gmail.com" style={{ color: C.indigo, fontWeight: 600 }}>assessmonk@gmail.com</a>.</P>
        </Section>

        <Section id="data-collected" title="2. Data We Collect">
          <P>We collect the following categories of data:</P>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { icon: '👤', label: 'Account data', desc: 'Name, email address, and profile image provided via Google OAuth login.' },
              { icon: '📋', label: 'Assessment data', desc: 'Clinical inputs you enter — patient demographics, subjective findings, objective measurements, and AI-generated outputs. This data is saved to your account.' },
              { icon: '🍪', label: 'Session data', desc: 'Authentication tokens and session identifiers stored in browser localStorage to keep you signed in.' },
              { icon: '📊', label: 'Usage data', desc: 'Anonymous usage patterns such as which modules are used and general interaction data, used to improve the platform.' },
            ].map(({ icon, label, desc }) => (
              <div key={label} style={{ background: C.snow, borderRadius: 12, padding: '14px 16px', border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: '1.2rem', marginBottom: 8 }}>{icon}</div>
                <div style={{ fontWeight: 800, fontSize: '0.82rem', color: C.navy, marginBottom: 5 }}>{label}</div>
                <div style={{ fontSize: '0.78rem', color: C.muted, lineHeight: 1.65 }}>{desc}</div>
              </div>
            ))}
          </div>
          <P>We do not collect actual patient personal data beyond what you voluntarily enter as part of creating an educational assessment record. You should not enter identifiable real patient data into the platform.</P>
        </Section>

        <Section id="google-oauth" title="3. Google Login (OAuth)">
          <P>We use Google OAuth 2.0 to authenticate users. When you sign in with Google:</P>
          <Ul items={[
            'Google shares your name, email address, and profile photo with AssessMonk.',
            'We do not receive or store your Google password.',
            'We use your Google email address as your account identifier.',
            'You can revoke AssessMonk\'s access at any time via your Google Account settings (myaccount.google.com → Security → Third-party apps).',
          ]} />
          <P>Google's own privacy practices are governed by the <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: C.indigo, fontWeight: 600 }}>Google Privacy Policy</a>.</P>
        </Section>

        <Section id="how-we-use" title="4. How We Use Your Data">
          <P>We use the data we collect for the following purposes:</P>
          <Ul items={[
            'To create and manage your AssessMonk account.',
            'To save and retrieve your clinical assessment records across sessions.',
            'To generate AI-assisted clinical output from your entered findings.',
            'To maintain session security via authentication tokens.',
            'To improve platform features using anonymised usage analytics.',
            'To respond to support inquiries you submit.',
          ]} />
          <P>We do not use your personal data for targeted advertising. We do not build advertising profiles. We do not sell your data to any third party.</P>
        </Section>

        <Section id="data-storage" title="5. Data Storage & Security">
          <P>AssessMonk stores data across the following infrastructure:</P>
          <Ul items={[
            'Assessment records and account data are stored on secure servers hosted via Render (render.com).',
            'The frontend application is hosted on Cloudflare Pages (cloudflare.com).',
            'Session tokens are stored in your browser\'s localStorage and are scoped to your device.',
            'All data is transmitted over encrypted HTTPS connections.',
          ]} />
          <P>While we implement industry-standard security practices, no system is completely immune to security risks. We encourage you not to enter identifiable real patient data into the platform.</P>
          <Highlight label="Important">
            AssessMonk is an educational platform. Do not input identifiable real patient health records. All assessment data should be anonymised or fictional for educational and practice purposes.
          </Highlight>
        </Section>

        <Section id="third-parties" title="6. Third-Party Services">
          <P>The following third-party services are used in the operation of AssessMonk:</P>
          {[
            { name: 'Google OAuth', purpose: 'User authentication and login', link: 'https://policies.google.com/privacy' },
            { name: 'Cloudflare Pages', purpose: 'Frontend hosting and delivery', link: 'https://www.cloudflare.com/privacypolicy/' },
            { name: 'Render', purpose: 'Backend API and database hosting', link: 'https://render.com/privacy' },
            { name: 'OpenAI (GPT-4o)', purpose: 'AI clinical reasoning engine', link: 'https://openai.com/policies/privacy-policy' },
          ].map(s => (
            <div key={s.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.snow, marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.85rem', color: C.navy }}>{s.name}</div>
                <div style={{ fontSize: '0.75rem', color: C.muted }}>{s.purpose}</div>
              </div>
              <a href={s.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.72rem', fontWeight: 700, color: C.indigo, textDecoration: 'none' }}>Privacy policy →</a>
            </div>
          ))}
        </Section>

        <Section id="data-retention" title="7. Data Retention">
          <P>Your assessment records are retained for as long as your account is active. You may delete individual assessment records at any time from the Assessment History section of your account.</P>
          <P>If you wish to delete your entire account and all associated data, please contact us at <a href="mailto:assessmonk@gmail.com" style={{ color: C.indigo, fontWeight: 600 }}>assessmonk@gmail.com</a>. We will process account deletion requests within 14 business days.</P>
        </Section>

        <Section id="cookies" title="8. Cookies & Local Storage">
          <P>AssessMonk uses browser localStorage (not traditional cookies) to store:</P>
          <Ul items={[
            'Your authentication session token, so you remain signed in across visits.',
            'In-progress assessment draft data, so you don\'t lose work between sessions.',
            'User preferences and UI state.',
          ]} />
          <P>You can clear this data at any time by clearing your browser's site data for assessmonk.com. Doing so will sign you out and clear any unsaved drafts.</P>
        </Section>

        <Section id="your-rights" title="9. Your Rights">
          <P>Depending on your location, you may have the following rights regarding your data:</P>
          <Ul items={[
            'Right to access — request a copy of the data we hold about you.',
            'Right to rectification — request correction of inaccurate data.',
            'Right to erasure — request deletion of your account and associated data.',
            'Right to portability — request export of your assessment records.',
            'Right to withdraw consent — revoke Google OAuth access at any time.',
          ]} />
          <P>To exercise any of these rights, contact us at <a href="mailto:assessmonk@gmail.com" style={{ color: C.indigo, fontWeight: 600 }}>assessmonk@gmail.com</a>.</P>
        </Section>

        <Section id="changes" title="10. Changes to This Policy">
          <P>We may update this Privacy Policy from time to time. When we do, we will update the "Last updated" date at the top of this page. For significant changes, we will notify users via the platform.</P>
          <P>Continued use of AssessMonk after changes are posted constitutes acceptance of the updated policy.</P>
        </Section>

        <Section id="contact" title="11. Contact">
          <P>For privacy-related questions, data requests, or concerns, please contact us:</P>
          <div style={{ background: C.indigoXL, borderRadius: 14, padding: '20px 24px', border: '1px solid #c7d2fe' }}>
            <div style={{ fontWeight: 800, fontSize: '0.95rem', color: C.navy, marginBottom: 6 }}>AssessMonk Privacy Team</div>
            <div style={{ fontSize: '0.88rem', color: C.muted, marginBottom: 4 }}>Email: <a href="mailto:assessmonk@gmail.com" style={{ color: C.indigo, fontWeight: 600 }}>assessmonk@gmail.com</a></div>
            <div style={{ fontSize: '0.88rem', color: C.muted }}>Website: <a href="https://assessmonk.com" style={{ color: C.indigo, fontWeight: 600 }}>assessmonk.com</a></div>
          </div>
        </Section>

      </div>

      {/* Footer */}
      <footer style={{ background: C.snow, padding: '24px clamp(20px,5vw,60px)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, borderTop: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src="/logo192.png" alt="AssessMonk" style={{ width: 22, height: 22, borderRadius: 6, objectFit: 'contain' }} />
          <span style={{ fontWeight: 800, fontSize: '0.82rem', color: C.navy }}>AssessMonk</span>
        </div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <Link to="/privacy"    style={{ fontSize: '0.72rem', color: C.indigo, textDecoration: 'none', fontWeight: 700 }}>Privacy Policy</Link>
          <Link to="/terms"      style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Terms</Link>
          <Link to="/disclaimer" style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Disclaimer</Link>
          <Link to="/refund"     style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Refund Policy</Link>
          <Link to="/contact"    style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Contact</Link>
        </div>
        <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>© 2026 AssessMonk. All rights reserved.</div>
      </footer>

    </div>
  );
}
